/**
 * BlogPost - Página individual de artigo
 * WorkStream Marketing Site
 */
import { useParams, Link } from 'react-router-dom'
import { ArrowLeft, Calendar, Clock, Share2, Bookmark, ThumbsUp } from 'lucide-react'
import Navbar from '../components/Navbar'
import Footer from '../components/Footer'

// Conteúdo de exemplo para o primeiro post
const sampleContent = `
## O que é a NR-35?

A Norma Regulamentadora nº 35 (NR-35) estabelece os requisitos mínimos e as medidas de proteção para o trabalho em altura, envolvendo o planejamento, a organização e a execução, de forma a garantir a segurança e a saúde dos trabalhadores envolvidos direta ou indiretamente com esta atividade.

### Definição de Trabalho em Altura

Considera-se trabalho em altura toda atividade executada acima de **2,00 metros (dois metros)** do nível inferior, onde haja risco de queda.

## Principais Requisitos

### 1. Análise de Risco

Antes de qualquer trabalho em altura, é obrigatória a análise de risco que deve considerar:

- **Risco de queda** de pessoas e materiais
- **Condições meteorológicas** adversas
- **Proximidade** de redes elétricas
- **Sinalização** e isolamento da área

### 2. Capacitação e Treinamento

Todo trabalhador deve receber treinamento periódico:

- **Carga horária mínima**: 8 horas
- **Validade**: 2 anos
- **Conteúdo programático** definido pela norma

### 3. Equipamentos de Proteção

Os EPIs obrigatórios incluem:

| Equipamento | Finalidade |
|-------------|-----------|
| Cinto de segurança tipo paraquedista | Proteção contra quedas |
| Talabarte com absorvedor de energia | Conexão e amortecimento |
| Capacete com jugular | Proteção da cabeça |
| Trava-quedas | Conexão ao ponto de ancoragem |

## Como o WorkStream Ajuda

O WorkStream automatiza a verificação de conformidade com a NR-35 através de:

1. **Verificação automática de EPIs** antes do acesso a áreas de altura
2. **Controle de validade** de treinamentos
3. **Registro fotográfico** de evidências
4. **Alertas em tempo real** para gestores

## Conclusão

A NR-35 é fundamental para a segurança dos trabalhadores. Com tecnologia, é possível garantir conformidade de forma eficiente e reduzir significativamente o risco de acidentes.

---

*Precisa de ajuda para implementar a NR-35 na sua empresa? [Entre em contato](/contato) com nossa equipe.*
`

// Posts relacionados
const relatedPosts = [
  {
    slug: 'nr-10-eletricidade',
    title: 'NR-10: Segurança em Serviços com Eletricidade',
    readTime: 14,
  },
  {
    slug: 'epi-deteccao-automatica',
    title: '5 Razões para Automatizar a Verificação de EPIs',
    readTime: 6,
  },
  {
    slug: 'lgpd-monitoramento-trabalho',
    title: 'LGPD e Monitoramento de Colaboradores',
    readTime: 15,
  },
]

export default function BlogPost() {
  const { slug: _slug } = useParams() // slug available but not used in this demo
  
  // Em produção, buscar do CMS/API
  const post = {
    title: 'NR-35: Guia Completo sobre Trabalho em Altura',
    excerpt: 'Tudo que você precisa saber sobre a NR-35, desde requisitos legais até implementação prática com tecnologia.',
    category: 'NRs',
    tags: ['NR-35', 'Segurança', 'Trabalho em Altura'],
    author: { name: 'Carlos Mendes', role: 'Especialista em Segurança' },
    publishedAt: new Date('2025-11-25'),
    readTime: 12,
    content: sampleContent,
  }
  
  return (
    <div className="min-h-screen bg-[#0B1120]">
      <Navbar />
      
      {/* Header */}
      <section className="pt-32 pb-12 relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-pattern opacity-30" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-gradient-to-br from-cyan-500/20 to-transparent rounded-full blur-3xl" />
        
        <div className="container mx-auto px-4 relative">
          <div className="max-w-3xl mx-auto">
            {/* Back link */}
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 text-slate-400 hover:text-white mb-6 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Voltar ao Blog
            </Link>
            
            {/* Category & Tags */}
            <div className="flex items-center gap-2 mb-4">
              <span className="px-3 py-1 rounded-lg text-sm font-medium bg-cyan-500/10 text-cyan-400">
                {post.category}
              </span>
              {post.tags.map(tag => (
                <span key={tag} className="px-2 py-1 rounded text-xs bg-slate-800 text-slate-400">
                  #{tag}
                </span>
              ))}
            </div>
            
            {/* Title */}
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
              {post.title}
            </h1>
            
            {/* Excerpt */}
            <p className="text-xl text-slate-400 mb-6">
              {post.excerpt}
            </p>
            
            {/* Meta */}
            <div className="flex flex-wrap items-center gap-6 text-sm text-slate-400">
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 rounded-full bg-cyan-500/20 flex items-center justify-center text-cyan-400 font-bold">
                  {post.author.name.charAt(0)}
                </div>
                <div>
                  <p className="text-white font-medium">{post.author.name}</p>
                  <p className="text-xs">{post.author.role}</p>
                </div>
              </div>
              
              <div className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                {post.publishedAt.toLocaleDateString('pt-BR', { day: 'numeric', month: 'long', year: 'numeric' })}
              </div>
              
              <div className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                {post.readTime} min de leitura
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Content */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            {/* Article Content */}
            <article className="prose prose-invert prose-lg max-w-none">
              {/* Renderização simplificada do markdown */}
              {post.content.split('\n').map((line, i) => {
                if (line.startsWith('## ')) {
                  return <h2 key={i} className="text-2xl font-bold text-white mt-8 mb-4">{line.replace('## ', '')}</h2>
                }
                if (line.startsWith('### ')) {
                  return <h3 key={i} className="text-xl font-semibold text-white mt-6 mb-3">{line.replace('### ', '')}</h3>
                }
                if (line.startsWith('- ')) {
                  return <li key={i} className="text-slate-300 ml-4">{line.replace('- ', '')}</li>
                }
                if (line.startsWith('1. ') || line.startsWith('2. ') || line.startsWith('3. ') || line.startsWith('4. ')) {
                  return <li key={i} className="text-slate-300 ml-4 list-decimal">{line.replace(/^\d+\.\s/, '')}</li>
                }
                if (line.trim() === '') return <br key={i} />
                if (line.startsWith('|')) return null // Skip table for simplicity
                if (line.startsWith('---')) return <hr key={i} className="border-slate-700 my-8" />
                if (line.startsWith('*')) return <p key={i} className="text-slate-400 italic">{line.replace(/\*/g, '')}</p>
                return <p key={i} className="text-slate-300 mb-4">{line}</p>
              })}
            </article>
            
            {/* Share & Actions */}
            <div className="flex items-center justify-between py-8 border-t border-b border-slate-800 my-8">
              <div className="flex items-center gap-4">
                <button className="flex items-center gap-2 px-4 py-2 rounded-xl bg-slate-800 text-slate-300 hover:text-white transition-colors">
                  <ThumbsUp className="w-4 h-4" />
                  Útil
                </button>
                <button className="flex items-center gap-2 px-4 py-2 rounded-xl bg-slate-800 text-slate-300 hover:text-white transition-colors">
                  <Bookmark className="w-4 h-4" />
                  Salvar
                </button>
              </div>
              
              <button className="flex items-center gap-2 px-4 py-2 rounded-xl bg-slate-800 text-slate-300 hover:text-white transition-colors">
                <Share2 className="w-4 h-4" />
                Compartilhar
              </button>
            </div>
            
            {/* Author Box */}
            <div className="p-6 rounded-2xl bg-slate-800/50 border border-slate-700 mb-8">
              <div className="flex items-start gap-4">
                <div className="w-16 h-16 rounded-full bg-cyan-500/20 flex items-center justify-center text-cyan-400 text-2xl font-bold">
                  {post.author.name.charAt(0)}
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-white">{post.author.name}</h4>
                  <p className="text-sm text-cyan-400 mb-2">{post.author.role}</p>
                  <p className="text-sm text-slate-400">
                    Especialista em segurança do trabalho com mais de 15 anos de experiência. 
                    Palestrante e consultor em NRs e compliance.
                  </p>
                </div>
              </div>
            </div>
            
            {/* Related Posts */}
            <div>
              <h3 className="text-xl font-semibold text-white mb-4">Artigos Relacionados</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {relatedPosts.map(related => (
                  <Link
                    key={related.slug}
                    to={`/blog/${related.slug}`}
                    className="p-4 rounded-xl bg-slate-800/50 border border-slate-700 hover:border-cyan-500/30 transition-colors"
                  >
                    <h4 className="font-medium text-white mb-2 line-clamp-2">{related.title}</h4>
                    <span className="text-xs text-slate-500">{related.readTime} min</span>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* CTA */}
      <section className="py-16 bg-gradient-to-br from-cyan-500/10 to-blue-600/10">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-2xl font-bold text-white mb-4">
            Quer automatizar a conformidade com NRs?
          </h2>
          <p className="text-slate-400 mb-6">
            Teste o WorkStream gratuitamente por 14 dias.
          </p>
          <Link
            to="/demo"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-white"
            style={{ background: 'linear-gradient(135deg, #00C6FF 0%, #0072FF 100%)' }}
          >
            Começar Teste Grátis
          </Link>
        </div>
      </section>
      
      <Footer />
    </div>
  )
}

